import { addDays, addWeeks, isBefore, subMonths, toDate } from 'date-fns'
import { AtendimentoProfissional, MedicoesAtendimentosAnterioresQuery } from 'graphql/types.generated'
import moment, { Moment } from 'moment'
import { maxBy, minBy } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { isAfterOrEqual, isBetween } from 'util/date/compare'
import { AcompanhamentoPreNatalQueryModel } from 'view/atendimentos/detail/soap/aside/pre-natal/model-acompanhamentoPreNatal'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { isEncerramentoGestacao } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { MedicoesPreNatal } from '../../types/model-historicoPreNatal'
import { PeriodoGestacaoModel } from '../../types/PeriodoGestacaoModel'
import { convertMedicaoOutrosAtendimentos } from '../converter-medicoes'
import { MedicaoAntropometricaPreNatal } from './model-medicoesPreNatal'
import {
  renderDataMedicao,
  renderEdema,
  renderIdadeGestacional,
  renderLabelIG,
  renderMedicaoPreNatal,
  renderMovimentacaoFetal,
  renderNumeroConsulta,
  renderPressaoArterial,
} from './render-medicoesPreNatal'

type MedicoesValidasReduced = {
  preGestacionais: MedicaoAntropometricaPreNatal[]
  gestacionais: MedicaoAntropometricaPreNatal[]
}

export function getMedicaoForImcReferencia(
  dataInicioGestacao: Date,
  medicoes: MedicaoAntropometricaPreNatal[]
): MedicaoAntropometricaPreNatal {
  if (isUndefinedOrNull(dataInicioGestacao) || isUndefinedOrNull(medicoes) || medicoes.isEmpty()) return null

  const { preGestacionais, gestacionais } = getMedicoesValidas(medicoes, dataInicioGestacao)

  if (!preGestacionais.isEmpty()) return maxBy(preGestacionais, (medicao) => medicao.dataMedicao)
  if (!gestacionais.isEmpty()) return minBy(gestacionais, (medicao) => medicao.dataMedicao)

  return null
}

function getMedicoesValidas(medicoes: MedicaoAntropometricaPreNatal[], dataInicioGestacao: Date) {
  return medicoes.reduce<MedicoesValidasReduced>(
    (acc, medicao) => {
      if (!hasPesoAndAltura(medicao)) return acc

      const dataMedicaoAsDate = toDate(medicao.dataMedicao)

      if (isMedicaoPreGestacionalValida(dataMedicaoAsDate, dataInicioGestacao)) acc.preGestacionais.push(medicao)
      if (isMedicaoGestacionalValida(dataMedicaoAsDate, dataInicioGestacao)) acc.gestacionais.push(medicao)

      return acc
    },
    { preGestacionais: [], gestacionais: [] }
  )
}

const hasPesoAndAltura = (medicao: MedicaoAntropometricaPreNatal): boolean =>
  !isUndefinedOrNull(medicao.altura) && !isUndefinedOrNull(medicao.peso)

const isMedicaoPreGestacionalValida = (dataMedicao: Date, dataInicioGestacao: Date): boolean =>
  isAfterOrEqual(dataMedicao, subMonths(dataInicioGestacao, 2)) && isBefore(dataMedicao, dataInicioGestacao)

const isMedicaoGestacionalValida = (dataMedicao: Date, dataInicioGestacao: Date): boolean =>
  isBetween(dataMedicao, dataInicioGestacao, addWeeks(dataInicioGestacao, 8))

export function getMedicoesPeso10SemanasAposInicioGestacao(
  dataInicioGestacao: Date,
  medicoesPeso: MedicaoAntropometricaPreNatal[]
): MedicaoAntropometricaPreNatal[] {
  if (isUndefinedOrNull(dataInicioGestacao) || isUndefinedOrNull(medicoesPeso) || medicoesPeso.isEmpty()) return null

  const noveSemanasQuatroDiasAposInicioGestacao = addDays(addWeeks(dataInicioGestacao, 9), 4)

  const medicoesValidas = medicoesPeso.filter(
    (medicao) =>
      !isUndefinedOrNull(medicao.peso) &&
      !isBefore(toDate(medicao.dataMedicao), noveSemanasQuatroDiasAposInicioGestacao)
  )

  return medicoesValidas
}

export function computeGanhoPesoGestacional(pesoReferencia: number, pesoConsulta: number): number {
  if (isUndefinedOrNull(pesoReferencia) || isUndefinedOrNull(pesoConsulta)) return null
  return pesoConsulta - pesoReferencia
}

export function renderGanhoPeso(pesoReferencia: number, pesoConsulta: number): string {
  const ganhoPeso = computeGanhoPesoGestacional(pesoReferencia, pesoConsulta)
  const sinal = ganhoPeso < -0.005 ? '-' : ''
  const magnitude = renderMedicaoPreNatal(Math.abs(ganhoPeso))

  return `${sinal}${magnitude}`
}

export function getPesoAtendimentoAtual(medicoesPeso: MedicaoAntropometricaPreNatal[]): number {
  return medicoesPeso.find((medicao) => medicao.origem === 'atendimentoAtual')?.peso
}

function hasAlturaOuPeso(medicao: HistoricoMedicaoModel): boolean {
  return !!medicao.valorPeso || !!medicao.valorAltura
}

function isMedicaoValida(medicao: HistoricoMedicaoModel, isEncerramentoGestacao: boolean): boolean {
  if (hasAlturaOuPeso(medicao)) {
    if (medicao.origem === 'atendimentoAtual' || medicao.origem === 'medicoesAnteriores') {
      return !isEncerramentoGestacao
    }

    return true
  }

  return false
}

export function getMedicoesAntropometricasPreNatal(
  medicoes: HistoricoMedicaoModel[] | ReadonlyArray<HistoricoMedicaoModel>,
  tipoPreNatal?: TipoPreNatal
): MedicaoAntropometricaPreNatal[] {
  const isEncerramento = isEncerramentoGestacao(tipoPreNatal)

  return medicoes
    .filter((medicao) => isMedicaoValida(medicao, isEncerramento))
    .map((medicao) => ({
      dataMedicao: medicao.dataMedicao,
      peso: medicao.valorPeso,
      altura: medicao.valorAltura,
      origem: medicao.origem,
    }))
}

export const getMedicoesAntropometricasPreNatalSorted = (medicoes: MedicoesAtendimentosAnterioresQuery['medicoes']) => {
  return (
    medicoes &&
    getMedicoesAntropometricasPreNatal([
      ...[...medicoes].sort((a, b) => b.dataMedicao - a.dataMedicao).map(convertMedicaoOutrosAtendimentos),
    ])
  )
}

export function getMedicaoAtualMaisRecente<
  T extends { id?: ID | null; dataMedicao?: Instant | null; medicaoAnterior?: boolean | null }
>(medicoes: ReadonlyArray<T | null>): T | undefined {
  return medicoes
    ?.filter((medicao): medicao is T => !!medicao && medicao.medicaoAnterior !== true)
    .reduce<T | undefined>((maisRecente, medicaoAtual) => {
      if (!maisRecente) return medicaoAtual

      const diffData = getTimestampMedicao(medicaoAtual.dataMedicao) - getTimestampMedicao(maisRecente.dataMedicao)
      const diffId = compareIdsNumericos(medicaoAtual.id, maisRecente.id)

      return diffData > 0 || (diffData === 0 && diffId > 0) ? medicaoAtual : maisRecente
    }, undefined)
}

const getTimestampMedicao = (dataMedicao?: Instant | null): number => {
  const timestamp = Number(dataMedicao)
  return isUndefinedOrNull(dataMedicao) || Number.isNaN(timestamp) ? Number.MIN_SAFE_INTEGER : timestamp
}

const compareIdsNumericos = (idA?: ID | null, idB?: ID | null): number => {
  if (idA === idB) return 0
  if (isUndefinedOrNull(idA)) return -1
  if (isUndefinedOrNull(idB)) return 1

  const idNormalizadoA = idA.replace(/^0+(?=\d)/, '')
  const idNormalizadoB = idB.replace(/^0+(?=\d)/, '')

  return idNormalizadoA.length - idNormalizadoB.length || idNormalizadoA.localeCompare(idNormalizadoB)
}

export const getColumnsTableItens = (
  dataInicioGestacao: Date,
  isIgEcografica: boolean,
  showRegistradoAgora: boolean,
  pesoReferencia: number,
  showPressaoArterial: boolean
) => {
  const columns = [
    {
      header: 'Consulta',
      name: 'numeroConsulta',
      render: (preNatal: MedicoesPreNatal) => renderNumeroConsulta(preNatal.numeroConsulta),
    },
    {
      header: 'Data da medição',
      name: 'dataMedicao',
      render: (preNatal: MedicoesPreNatal) => renderDataMedicao(preNatal.dataDaMedicao),
    },
    {
      header: renderLabelIG(isIgEcografica, showRegistradoAgora),
      name: 'idadeGestacional',
      render: (preNatal: MedicoesPreNatal) => renderIdadeGestacional(dataInicioGestacao, preNatal.dataDaMedicao),
    },
    {
      header: 'Edema',
      name: 'edema',
      render: (preNatal: MedicoesPreNatal) => renderEdema(preNatal?.edema),
    },
    {
      header: 'Altura uterina (cm)',
      name: 'alturaUterina',
      render: (preNatal: MedicoesPreNatal) => renderMedicaoPreNatal(preNatal.alturaUterina),
    },
    {
      header: 'Ganho de peso (kg)',
      name: 'ganhoPeso',
      render: (preNatal: MedicoesPreNatal) => renderGanhoPeso(pesoReferencia, preNatal.peso),
    },
    {
      header: 'BCF 1 (bpm)',
      name: 'batimentoCardiacoFetal',
      render: (preNatal: MedicoesPreNatal) => renderMedicaoPreNatal(preNatal.batimentoCardiacoFetal),
    },
    {
      header: 'BCF 2 (bpm)',
      name: 'batimentoCardiacoFetal2',
      render: (preNatal: MedicoesPreNatal) => renderMedicaoPreNatal(preNatal.batimentoCardiacoFetal2),
    },
    {
      header: showPressaoArterial ? 'Mov. fetal' : 'Movimentação fetal',
      name: 'movimentacaoFetal',
      render: (preNatal: MedicoesPreNatal) =>
        renderMovimentacaoFetal(preNatal?.movimentacaoFetal, preNatal.isRegistradoAgora),
    },
  ]

  showPressaoArterial &&
    columns.push({
      header: 'Pressão arterial (mmHg)',
      name: 'pressaoArterial',
      render: (preNatal: MedicoesPreNatal) => renderPressaoArterial(preNatal?.pressaoArterial),
    })

  return columns
}

export const getEndGestacao = (gestacao: PeriodoGestacaoModel): Moment => {
  if (gestacao.fim && moment(gestacao.fim).isAfter(gestacao.inicio)) {
    return moment(gestacao.fim).endOf('day')
  }

  const endOfToday = moment().endOf('day')
  const endOf42Weeks = moment(gestacao.inicio).add(42, 'week').endOf('day')
  const endOf48Weeks = moment(gestacao.inicio).add(48, 'week').endOf('day')

  if (endOfToday.isAfter(endOf42Weeks)) {
    return endOfToday.isBefore(endOf48Weeks) ? endOfToday : endOf48Weeks
  }

  return endOf42Weeks
}

export const filtrarAtendimentosPeriodoGestacional = (
  atendimentosProfUltimaGestacao: ReadonlyArray<AcompanhamentoPreNatalQueryModel | AtendimentoProfissional>,
  dataInicioGestacao: Date
): ReadonlyArray<AcompanhamentoPreNatalQueryModel | AtendimentoProfissional> => {
  if (!dataInicioGestacao || !atendimentosProfUltimaGestacao.length) return atendimentosProfUltimaGestacao ?? []

  return atendimentosProfUltimaGestacao.filter((atendimento) => {
    const dataAtendimento = getMedicaoAtualMaisRecente(atendimento.medicoes)?.dataMedicao ?? atendimento.iniciadoEm
    return dataAtendimento && !isBefore(dataAtendimento, dataInicioGestacao)
  })
}
